import { Avatar } from "@mui/material";
import { Account } from "@rms-types/modules/account/entities/account";
import { Department } from "@rms-types/modules/department/entities/department";
import { LicenseType } from "@rms-types/modules/license-type/entities/license-type";
import { Role } from "@rms-types/modules/role/entities/role";
import { FieldValues } from "react-hook-form";

import { AsyncMultiSelectSchema, AsyncSelectSchema } from "@/components/section";
import { accountEndpoint, departmentEndpoint, licenseTypeEndpoint, roleEndpoint } from "@/constants";
import stringAvatar from "@/helpers/stringAvatar";

export type AsyncMultiSelectOptions<TFormValues extends FieldValues, TRecord> = Pick<
  AsyncMultiSelectSchema<TFormValues, TRecord>,
  "name"
> &
  Partial<Omit<AsyncMultiSelectSchema<TFormValues, TRecord>, "type">>;

export type AsyncSelectOptions<TFormValues extends FieldValues, TRecord> = Pick<
  AsyncSelectSchema<TFormValues, TRecord>,
  "name"
> &
  Partial<Omit<AsyncSelectSchema<TFormValues, TRecord>, "type">>;

function accountAsyncMultiSelect<TFormValues extends FieldValues>(
  options: AsyncMultiSelectOptions<TFormValues, Account>
): AsyncMultiSelectSchema<TFormValues, Account> {
  return {
    type: "async-multi-select",
    endpoint: accountEndpoint.account,
    getOptionLabel: (record) => record.name,
    getOptionValue: (record) => record.id,
    sortBy: (a, b) => a.name.localeCompare(b.name),
    getOptionIcon: (option) => <Avatar {...stringAvatar(option.name, "small")} />,
    ...options,
  };
}

function licenseTypeAsyncSelect<TFormValues extends FieldValues>(
  options: AsyncSelectOptions<TFormValues, LicenseType>
): AsyncSelectSchema<TFormValues, LicenseType> {
  return {
    type: "async-select",
    endpoint: licenseTypeEndpoint.getLicenseTypeWithParams({ is_parent: false }),
    getOptionLabel: (record) => record.name,
    getOptionValue: (record) => record.id,
    sortBy: (a, b) => a.name.localeCompare(b.name),
    getOptionIcon: (option) => <Avatar {...stringAvatar(option.name, "small")} />,
    ...options,
  };
}

function departmentAsyncMultiSelect<TFormValues extends FieldValues>(
  options: AsyncMultiSelectOptions<TFormValues, Department>
): AsyncMultiSelectSchema<TFormValues, Department> {
  return {
    type: "async-multi-select",
    endpoint: departmentEndpoint.department,
    getOptionLabel: (record) => record.name,
    getOptionValue: (record) => record.id,
    getFilteredOptions: (data) => data.sort((a, b) => a.name.localeCompare(b.name)),
    ...options,
  };
}

function roleAsyncMultiSelect<TFormValues extends FieldValues>(
  options: AsyncMultiSelectOptions<TFormValues, Role>
): AsyncMultiSelectSchema<TFormValues, Role> {
  return {
    type: "async-multi-select",
    endpoint: roleEndpoint.role,
    getOptionLabel: (record) => record.name,
    getOptionValue: (record) => record.id,
    getFilteredOptions: (data) => data.sort((a, b) => a.name.localeCompare(b.name)),
    ...options,
  };
}

export const schemas = {
  accountAsyncMultiSelect,
  departmentAsyncMultiSelect,
  roleAsyncMultiSelect,
  licenseTypeAsyncSelect,
};
